import { useCallback, useMemo } from 'react';

import { useFctReport, useHiRadiologistKeyImages } from '@/shared/hook';
import { REPORT_LINK_KEY } from '@/shared/model';
import { useKeyImageLabelsStore, useReportValidationStore } from '@/store';

export function useUpdateKeyImagesStatus() {
  const { keyImages } = useHiRadiologistKeyImages();
  const { report } = useFctReport();

  const setStatus = useReportValidationStore((state) => state.setStatus);
  const keyImageLabels = useKeyImageLabelsStore((state) => state.keyImageLabels);

  const nonDeprecatedKeyImageLabels = keyImageLabels.filter(
    (label) => label.status !== 'deprecated',
  );

  const excludedSet = new Set(report?.excludeDicomImages || []);
  const labeledSet = new Set(
    nonDeprecatedKeyImageLabels
      .filter((image) => image.imageKey && image.instanceKey)
      .map((label) => label.imageKey),
  );
  const processedCount = keyImages.reduce(
    (count, { imageKey }) =>
      count + (excludedSet.has(imageKey) || labeledSet.has(imageKey) ? 1 : 0),
    0,
  );

  const hasError = useMemo(
    () =>
      processedCount !== keyImages.length ||
      nonDeprecatedKeyImageLabels.length > keyImageLabels.length ||
      nonDeprecatedKeyImageLabels.length !== labeledSet.size,
    [
      processedCount,
      keyImages.length,
      nonDeprecatedKeyImageLabels.length,
      keyImageLabels.length,
      labeledSet.size,
    ],
  );

  return useCallback(() => {
    setStatus(REPORT_LINK_KEY.KEY_IMAGES, {
      done: false,
      error: hasError,
      warning: false,
    });

    return hasError;
  }, [hasError, setStatus]);
}
